/* Temporary first-party measurement. No email, cart key, URL or customer data is sent. */
(() => {
  if (window.SKULL_ROULETTE_TRACKER) return;
  const options = window.SKULL_ROULETTE_TRACKING_CONFIG || {};
  const localTest = ['localhost', '127.0.0.1', '[::1]'].includes(location.hostname) && options.testMode === true;
  const excluded = window.Shopify?.designMode || (window.Shopify?.theme?.role && window.Shopify.theme.role !== 'main') ||
    new URLSearchParams(location.search).has('preview_theme_id') || window.top !== window.self || navigator.webdriver;
  if (excluded && !localTest) return;
  if (!localTest && !['skull.mx', 'www.skull.mx', 'jewerlyking.myshopify.com'].includes(location.hostname)) return;
  const campaign = options.campaign || 'skull-slot-20261014';
  const endpoint = String(options.endpoint || 'https://dhas.skull.mx/api/roulette').replace(/\/$/, '');
  try {
    const origin = new URL(endpoint).origin;
    if (localTest ? origin !== location.origin : origin !== 'https://dhas.skull.mx') return;
  } catch (_) { return; }
  if (!window.crypto?.randomUUID || !window.crypto?.subtle || typeof window.fetch !== 'function') return;

  const request = window.fetch.bind(window);
  const lifecycle = new AbortController();
  // Keep the backend campaign contract while isolating this offer's browser state.
  const storageSuffix = options.offer === '5x1' ? '-5x1-v1' : '-v1';
  const sessionKey = `skull-roulette-session${storageSuffix}`;
  const attributionKey = `skull-roulette-attribution${storageSuffix}`;
  const outboxKey = `skull-roulette-outbox${storageSuffix}`;
  const sessionDuration = 30 * 60 * 1000;
  const maxAttribution = 7 * 24 * 60 * 60 * 1000;
  const uuidPattern = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i;
  const types = new Set(['view', 'spin', 'reward_view', 'email_attempt', 'email_accepted', 'email_error', 'dismiss', 'cart_bound', 'checkout_intent']);
  const phases = new Set(['intro', 'spinning', 'winning', 'result', 'storefront']);
  const sources = new Set(['popup', 'button', 'lever', 'form', 'klaviyo', 'timeout', 'rate_limit', 'network', 'close', 'decline', 'escape', 'backdrop', 'cart', 'checkout']);
  let remote = null, attribution = null, memorySession = null, consent = false, enabled = false, suspended = false, generation = 0;
  let popupTracked = false, loadingPrivacy = false, privacyAttempts = 0, stopped = false;
  let queue = [], flushTimer = 0, expiryTimer = 0, configTimer = 0, privacyTimer = 0, cartTimer = 0;
  let configRequest = null, cartRequest = null, lastConfigAt = 0, lastReplayAt = 0;
  const inFlight = new Set(), pendingHashes = new Set(), retryTimers = new Set(), pageBindings = new Set();
  const outbox = new Map(), transmitting = new Set();
  const device = matchMedia('(max-width: 767px)').matches ? 'mobile' : 'desktop';

  function read(storage, key) {
    try { return JSON.parse(window[storage].getItem(key) || 'null'); } catch (_) { return null; }
  }
  function write(storage, key, value) {
    try { window[storage].setItem(key, JSON.stringify(value)); } catch (_) {}
  }
  function clearStored() {
    try { localStorage.removeItem(attributionKey); } catch (_) {}
    try { localStorage.removeItem(outboxKey); } catch (_) {}
    try { sessionStorage.removeItem(sessionKey); } catch (_) {}
  }
  function allowed() {
    try { return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed() === true; } catch (_) { return false; }
  }
  function remoteActive() {
    const now = Date.now();
    return remote?.enabled === true && remote.campaign === campaign && Number.isFinite(remote.start) &&
      now >= remote.start && (remote.end === null || (Number.isFinite(remote.end) && now < remote.end));
  }
  function active() {
    return !stopped && !suspended && enabled && consent && remoteActive() && allowed();
  }
  function validAttribution(value) {
    const now = Date.now();
    return value && value.campaign === campaign && uuidPattern.test(value.exposure_id) && uuidPattern.test(value.session_id) &&
      Number.isFinite(value.viewed_at) && value.viewed_at <= now + 60000 && Number.isFinite(value.expires_at) &&
      value.expires_at > now && value.expires_at <= value.viewed_at + maxAttribution && value.flags &&
      typeof value.flags === 'object' && !Array.isArray(value.flags) && canonicalView(value);
  }
  function canonicalView(value) {
    const view = value?.view_event;
    if (!view || view.type !== 'view' || !uuidPattern.test(view.id) || view.session_id !== value.session_id ||
      view.exposure_id !== value.exposure_id || !Number.isFinite(Date.parse(view.at))) return null;
    // Whitelist again when reading storage; never echo arbitrary stored properties.
    return { id: view.id, type: 'view', session_id: view.session_id, exposure_id: view.exposure_id, at: new Date(Date.parse(view.at)).toISOString(),
      phase: phases.has(view.phase) ? view.phase : 'intro', source: 'popup', device: view.device === 'mobile' ? 'mobile' : 'desktop' };
  }
  function saveAttribution() {
    if (active() && validAttribution(attribution)) write('localStorage', attributionKey, attribution);
  }
  function cleanPending(item) {
    const event = item?.event, now = Date.now();
    if (!event || !types.has(event.type) || !uuidPattern.test(event.id) || !uuidPattern.test(event.session_id) ||
      !uuidPattern.test(event.exposure_id) || !Number.isFinite(Date.parse(event.at)) ||
      Date.parse(event.at) < now - 86400000 || Date.parse(event.at) > now + 300000 ||
      !phases.has(event.phase) || !sources.has(event.source)) return null;
    const view = canonicalView({ view_event: item.view, session_id: event.session_id, exposure_id: event.exposure_id });
    if (!view || Date.parse(view.at) < now - maxAttribution || Date.parse(view.at) > now + 300000) return null;
    const clean = { id: event.id, type: event.type, session_id: event.session_id, exposure_id: event.exposure_id,
      at: new Date(Date.parse(event.at)).toISOString(), phase: event.phase, source: event.source,
      device: event.device === 'mobile' ? 'mobile' : 'desktop' };
    if (event.type === 'cart_bound') {
      if (!/^[a-f0-9]{64}$/.test(event.cart_hash || '')) return null;
      clean.cart_hash = event.cart_hash;
    }
    return { event: clean, view };
  }
  function persistOutbox(acknowledged = []) {
    if (!active()) return;
    const removed = new Set(acknowledged), saved = read('localStorage', outboxKey);
    const candidates = saved?.campaign === campaign && Array.isArray(saved.items) ? saved.items : [];
    const merged = new Map();
    for (const item of [...candidates, ...outbox.values()]) {
      const clean = cleanPending(item);
      if (clean && !removed.has(clean.event.id)) merged.set(clean.event.id, clean);
    }
    // Bound retries and storage even during a long outage or repeated form errors.
    const retained = [...merged.values()].sort((a, b) => Date.parse(a.event.at) - Date.parse(b.event.at)).slice(-40);
    outbox.clear(); for (const item of retained) outbox.set(item.event.id, item);
    if (retained.length) write('localStorage', outboxKey, { campaign, items: retained });
    else try { localStorage.removeItem(outboxKey); } catch (_) {}
  }
  function resumeDelivery(force = false) {
    if (!active() || (!force && Date.now() - lastReplayAt < 5000)) return;
    lastReplayAt = Date.now(); persistOutbox();
    const queued = new Set(queue.map(item => item.event.id));
    for (const item of outbox.values()) {
      if (!queued.has(item.event.id) && !transmitting.has(item.event.id)) queue.push(item);
    }
    if (queue.length && !flushTimer) flushTimer = setTimeout(flush, 400);
  }
  function clearTracking({ preserveStored = false } = {}) {
    enabled = false; queue = []; generation++;
    if (!preserveStored) { attribution = null; memorySession = null; popupTracked = false; outbox.clear(); clearStored(); }
    clearTimeout(flushTimer); flushTimer = 0;
    clearTimeout(cartTimer); cartTimer = 0;
    clearTimeout(expiryTimer); expiryTimer = 0;
    cartRequest?.abort(); cartRequest = null;
    for (const timer of retryTimers) clearTimeout(timer);
    retryTimers.clear();
    for (const controller of inFlight) controller.abort();
    inFlight.clear(); pendingHashes.clear(); pageBindings.clear(); transmitting.clear();
  }
  function currentAttribution() {
    if (!validAttribution(attribution)) { attribution = null; return null; }
    return attribution;
  }
  function touchSession() {
    const session = read('sessionStorage', sessionKey) || memorySession;
    if (session && session.id === attribution?.session_id) {
      session.last_at = Date.now(); memorySession = session; write('sessionStorage', sessionKey, session);
    }
  }
  function newExposure() {
    let session = read('sessionStorage', sessionKey) || memorySession;
    const now = Date.now();
    if (!session || !uuidPattern.test(session.id) || !Number.isFinite(session.last_at) || now - session.last_at >= sessionDuration || now < session.last_at) {
      session = { id: crypto.randomUUID(), last_at: now };
    }
    session.last_at = now; memorySession = session; write('sessionStorage', sessionKey, session);
    const days = Math.min(7, Math.max(1, Number(remote.attribution_days) || 7));
    attribution = { campaign, session_id: session.id, exposure_id: crypto.randomUUID(), viewed_at: now,
      expires_at: now + days * 86400000, flags: {} };
    popupTracked = true;
  }
  function eventPayload(type, details) {
    const value = { id: type === 'cart_bound' && uuidPattern.test(details.event_id || '') ? details.event_id : crypto.randomUUID(), type, session_id: attribution.session_id, exposure_id: attribution.exposure_id,
      at: new Date().toISOString(), phase: phases.has(details.phase) ? details.phase : 'storefront',
      source: sources.has(details.source) ? details.source : 'popup', device };
    if (type === 'cart_bound' && /^[a-f0-9]{64}$/.test(details.cart_hash || '')) value.cart_hash = details.cart_hash;
    return value;
  }
  function track(type, details = {}) {
    if (!types.has(type)) return;
    if (type === 'view') popupTracked = false;
    if (!active()) { if (enabled && (!allowed() || !remoteActive())) refreshState(); return; }
    if (type === 'view') {
      if (document.hidden) return;
      newExposure();
    } else if (!currentAttribution()) return;
    if (type === 'dismiss' && details.source === 'email') return;
    if (type === 'cart_bound' && !/^[a-f0-9]{64}$/.test(details.cart_hash || '')) return;
    if (type === 'cart_bound') {
      const binding = `${attribution.exposure_id}:${details.cart_hash}`;
      if (pageBindings.has(binding)) return;
      pageBindings.add(binding);
    } else if (!['email_attempt', 'email_error'].includes(type)) {
      if (attribution.flags[type]) return;
      attribution.flags[type] = true;
    }
    const event = eventPayload(type, details);
    if (type === 'view') attribution.view_event = event;
    if (queue.some(item => item.event.id === event.id) || transmitting.has(event.id)) return;
    const pending = outbox.get(event.id) || { event, view: canonicalView(attribution) };
    outbox.set(event.id, pending); persistOutbox(); queue.push(pending);
    if (queue.length > 60) queue.splice(0, queue.length - 60);
    touchSession(); saveAttribution();
    if (type === 'view') bindCurrentCart();
    if (!flushTimer) flushTimer = setTimeout(flush, 400);
    if (queue.length >= 20) flush();
  }
  async function sendBatch(batch, attempt = 0) {
    if (!active() || !batch.length) return;
    const startedGeneration = generation;
    for (const event of batch) transmitting.add(event.id);
    let retryScheduled = false;
    const controller = new AbortController(); inFlight.add(controller);
    const timeout = setTimeout(() => controller.abort(), 5000);
    try {
      const response = await request(`${endpoint}/events`, {
        method: 'POST', credentials: 'omit', mode: 'cors', keepalive: true, referrerPolicy: 'no-referrer',
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
        body: JSON.stringify({ campaign, events: batch }), signal: controller.signal
      });
      if (response.status === 403 || response.status === 410) { remote = null; clearTracking(); return; }
      if (!response.ok && (response.status === 429 || response.status >= 500)) throw new Error('retry');
      if (response.ok && active() && generation === startedGeneration) persistOutbox(batch.map(event => event.id));
    } catch (_) {
      if (attempt === 0 && active() && generation === startedGeneration) {
        retryScheduled = true;
        const timer = setTimeout(() => {
          retryTimers.delete(timer);
          if (active() && generation === startedGeneration) void sendBatch(batch, 1);
        }, 1000);
        retryTimers.add(timer);
      } else if (generation === startedGeneration) {
        for (const event of batch) if (event.type === 'cart_bound') pageBindings.delete(`${event.exposure_id}:${event.cart_hash}`);
      }
    } finally {
      clearTimeout(timeout); inFlight.delete(controller);
      if (!retryScheduled && generation === startedGeneration) for (const event of batch) transmitting.delete(event.id);
    }
  }
  function flush() {
    clearTimeout(flushTimer); flushTimer = 0;
    if (!active()) { queue = []; return; }
    while (queue.length) {
      const batch = [], exposures = new Set();
      while (queue.length) {
        const item = queue[0], needsView = !exposures.has(item.event.exposure_id) && item.event.type !== 'view';
        if (batch.length + (needsView ? 2 : 1) > 20) break;
        queue.shift();
        if (needsView) batch.push(item.view);
        exposures.add(item.event.exposure_id); batch.push(item.event);
      }
      // Echo the same view ID in every batch so retries/navigation cannot orphan actions.
      void sendBatch(batch);
    }
  }
  async function bindCart(cart) {
    if (!active() || !currentAttribution() || typeof cart?.token !== 'string') return;
    // ?key is an access credential. Hash only the opaque cart ID, as on Admin orders.
    const token = cart.token.trim().split('?')[0];
    if (!token || token.length > 1024) return;
    const exposure = attribution.exposure_id;
    const pendingKey = `${exposure}:${token}`;
    if (pendingHashes.has(pendingKey)) return;
    pendingHashes.add(pendingKey);
    try {
      const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(token));
      const cartHash = Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
      // Replay the same anonymous binding on each page without creating duplicate events.
      // A failed POST must never persistently suppress the cart-to-order relationship.
      const bindingDigest = new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(`${campaign}:${exposure}:${cartHash}`)));
      bindingDigest[6] = (bindingDigest[6] & 15) | 80; bindingDigest[8] = (bindingDigest[8] & 63) | 128;
      const bindingHex = Array.from(bindingDigest.slice(0, 16), byte => byte.toString(16).padStart(2, '0')).join('');
      const eventId = `${bindingHex.slice(0,8)}-${bindingHex.slice(8,12)}-${bindingHex.slice(12,16)}-${bindingHex.slice(16,20)}-${bindingHex.slice(20)}`;
      if (active() && attribution?.exposure_id === exposure) track('cart_bound', { phase: 'storefront', source: 'cart', cart_hash: cartHash, event_id: eventId });
    } catch (_) {} finally { pendingHashes.delete(pendingKey); }
  }
  function bindCurrentCart() {
    if (!active() || !currentAttribution()) return;
    if (window.__SKULL_CART?.token) { void bindCart(window.__SKULL_CART); return; }
    if (cartTimer || cartRequest) return;
    cartTimer = setTimeout(async () => {
      cartTimer = 0;
      if (!active() || !currentAttribution()) return;
      if (window.__SKULL_CART?.token) { void bindCart(window.__SKULL_CART); return; }
      const controller = new AbortController(); cartRequest = controller;
      const timeout = setTimeout(() => controller.abort(), 4000);
      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await request(`${root}cart.js`, { credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
        if (response.ok && active()) await bindCart(await response.json());
      } catch (_) {} finally { clearTimeout(timeout); if (cartRequest === controller) cartRequest = null; }
    }, 700);
  }
  function captureVisiblePopup() {
    const popup = document.querySelector('#skull-slot-popup[open]');
    if (!popup || popupTracked || document.hidden || !active()) return;
    // Consent/config may arrive after showModal. Count only a popup still visible now.
    track('view', { phase: popup.dataset.phase || 'intro', source: 'popup' });
    if (popup.dataset.phase === 'result') track('reward_view', { phase: 'result', source: 'popup' });
  }
  function refreshState() {
    if (typeof window.Shopify?.customerPrivacy?.analyticsProcessingAllowed !== 'function') return;
    consent = allowed();
    if (!consent) { clearTracking(); return; }
    // A page navigation must not erase attribution while its config request loads.
    if (!remote) return;
    if (!remoteActive()) { clearTracking(); return; }
    if (stopped || suspended) return;
    const wasEnabled = enabled;
    enabled = true;
    if (!wasEnabled) {
      const stored = read('localStorage', attributionKey) || attribution;
      attribution = validAttribution(stored) ? stored : null;
      resumeDelivery(true);
    }
    clearTimeout(expiryTimer);
    expiryTimer = Number.isFinite(remote.end) ? setTimeout(refreshState, Math.min(2147483647, Math.max(1, remote.end - Date.now()))) : 0;
    captureVisiblePopup(); bindCurrentCart();
  }
  function loadPrivacy() {
    if (stopped) return;
    if (typeof window.Shopify?.customerPrivacy?.analyticsProcessingAllowed === 'function') { refreshState(); return; }
    if (loadingPrivacy) return;
    if (typeof window.Shopify?.loadFeatures !== 'function') {
      if (++privacyAttempts <= 10) privacyTimer = setTimeout(loadPrivacy, 500);
      return;
    }
    loadingPrivacy = true;
    try {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], error => {
        loadingPrivacy = false;
        if (error) { consent = false; clearTracking(); return; }
        refreshState();
      });
    } catch (_) { loadingPrivacy = false; consent = false; clearTracking(); }
  }
  async function loadConfig() {
    if (stopped || configRequest) return;
    const controller = new AbortController(); configRequest = controller;
    const timeout = setTimeout(() => controller.abort(), 5000);
    try {
      const response = await request(`${endpoint}/config`, { credentials: 'omit', mode: 'cors', cache: 'no-store', referrerPolicy: 'no-referrer', signal: controller.signal });
      if (!response.ok) throw new Error('configuration');
      const value = await response.json();
      remote = { enabled: value.enabled === true, campaign: value.campaign, start: Date.parse(value.starts_at),
        end: value.ends_at === null ? null : Date.parse(value.ends_at), attribution_days: value.attribution_days };
      lastConfigAt = Date.now(); refreshState();
    } catch (_) { remote = null; clearTracking({ preserveStored: true }); }
    finally {
      clearTimeout(timeout); if (configRequest === controller) configRequest = null;
      clearTimeout(configTimer);
      if (!stopped && !suspended) configTimer = setTimeout(loadConfig, 300000);
    }
  }
  function checkoutTarget(target) {
    if (!(target instanceof Element)) return false;
    const control = target.closest('button[name="checkout"],input[name="checkout"],a[href]');
    if (!control || control.disabled) return false;
    if (control.matches('[name="checkout"]')) return true;
    try { return /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?checkout(?:\/|$)/i.test(new URL(control.href, location.href).pathname); } catch (_) { return false; }
  }

  window.SKULL_ROULETTE_TRACKER = { track };
  document.addEventListener('skull:cart-synced', event => { void bindCart(event.detail?.cart); }, { signal: lifecycle.signal });
  document.addEventListener('click', event => {
    if (checkoutTarget(event.target)) track('checkout_intent', { phase: 'storefront', source: 'checkout' });
  }, { capture: true, signal: lifecycle.signal });
  document.addEventListener('submit', event => {
    if (checkoutTarget(event.submitter)) track('checkout_intent', { phase: 'storefront', source: 'checkout' });
  }, { capture: true, signal: lifecycle.signal });
  document.addEventListener('visitorConsentCollected', refreshState, { signal: lifecycle.signal });
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) flush();
    else {
      refreshState();
      resumeDelivery();
      if (Date.now() - lastConfigAt > 60000) void loadConfig();
    }
  }, { signal: lifecycle.signal });
  window.addEventListener('online', () => {
    if (remoteActive()) { refreshState(); resumeDelivery(); }
    else void loadConfig();
  }, { signal: lifecycle.signal });
  window.addEventListener('pagehide', event => {
    flush(); suspended = true;
    // Keepalive may finish while cached. Its old acknowledgement cannot clear a resumed delivery.
    generation++; transmitting.clear(); pageBindings.clear();
    clearTimeout(cartTimer); cartTimer = 0; cartRequest?.abort(); cartRequest = null;
    clearTimeout(configTimer); clearTimeout(expiryTimer); clearTimeout(privacyTimer);
    for (const timer of retryTimers) clearTimeout(timer);
    retryTimers.clear();
    if (!event.persisted) { stopped = true; lifecycle.abort(); }
  }, { signal: lifecycle.signal });
  window.addEventListener('pageshow', event => {
    if (!event.persisted) return;
    suspended = false; refreshState(); resumeDelivery(true); void loadConfig();
  }, { signal: lifecycle.signal });
  loadPrivacy(); void loadConfig();
})();
